// Lab-parameter localization.
//
// Values are stored in the DB as raw English strings (from the seeded reference
// ranges). This helper renders them in the current UI language without changing
// what's stored on the server — that keeps DB rows portable across languages
// and lets historical consultations still find their reference-range match.

const LAB_LABELS = {
  // Biochemistry
  Glucose:        { ro: "Glucoză",          en: "Glucose" },
  BUN:            { ro: "Uree (BUN)",       en: "BUN" },
  Creatinine:     { ro: "Creatinină",       en: "Creatinine" },
  ALT:            { ro: "ALT (TGP)",        en: "ALT" },
  ALP:            { ro: "Fosfatază alcalină", en: "ALP" },
  "Total Protein":{ ro: "Proteine totale",  en: "Total Protein" },
  AST:            { ro: "AST (TGO)",        en: "AST" },
  Albumin:        { ro: "Albumină",         en: "Albumin" },
  Globulin:       { ro: "Globuline",        en: "Globulin" },
  Bilirubin:      { ro: "Bilirubină",       en: "Bilirubin" },
  Amylase:        { ro: "Amilază",          en: "Amylase" },
  Lipase:         { ro: "Lipază",           en: "Lipase" },
  Calcium:        { ro: "Calciu",           en: "Calcium" },
  Phosphorus:     { ro: "Fosfor",           en: "Phosphorus" },
  Sodium:         { ro: "Sodiu",            en: "Sodium" },
  Potassium:      { ro: "Potasiu",          en: "Potassium" },
  Chloride:       { ro: "Clor",             en: "Chloride" },
  Cholesterol:    { ro: "Colesterol",       en: "Cholesterol" },
  Triglycerides:  { ro: "Trigliceride",     en: "Triglycerides" },
  // CBC / hematology
  WBC:            { ro: "Leucocite (WBC)",  en: "WBC" },
  RBC:            { ro: "Eritrocite (RBC)", en: "RBC" },
  Hemoglobin:     { ro: "Hemoglobină",      en: "Hemoglobin" },
  Hematocrit:     { ro: "Hematocrit",       en: "Hematocrit" },
  Platelets:      { ro: "Trombocite",       en: "Platelets" },
  Neutrophils:    { ro: "Neutrofile",       en: "Neutrophils" },
  Lymphocytes:    { ro: "Limfocite",        en: "Lymphocytes" },
  Monocytes:      { ro: "Monocite",         en: "Monocytes" },
  Eosinophils:    { ro: "Eozinofile",       en: "Eosinophils" },
  Basophils:      { ro: "Bazofile",         en: "Basophils" },
  MCV:            { ro: "VEM (MCV)",        en: "MCV" },
  MCH:            { ro: "HEM (MCH)",        en: "MCH" },
  MCHC:           { ro: "CHEM (MCHC)",      en: "MCHC" },
};

export function labLabel(param, lang = "ro") {
  if (!param) return "";
  const entry = LAB_LABELS[param];
  if (!entry) return param;              // unknown → passthrough (custom entries)
  return entry[lang] || entry.en || param;
}

// Reverse-lookup: given a localized label the user picked from a datalist,
// return the canonical English string to store. Falls back to the raw value.
export function labCanonical(display) {
  if (!display) return "";
  for (const [en, tr] of Object.entries(LAB_LABELS)) {
    if (tr.ro === display || tr.en === display || en === display) return en;
  }
  return display;
}

// Lenient canonical key used for de-duplicating equivalent parameters that are
// written differently across languages/abbreviations, e.g. the Romanian default
// "Leucocite" vs the seeded English "WBC" (LAB_LABELS.WBC.ro = "Leucocite (WBC)").
// Strips parenthetical abbreviations on both sides before matching.
export function labCanonicalLoose(display) {
  if (!display) return "";
  const strip = (s) =>
    String(s || "").toLowerCase().replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
  const target = strip(display);
  for (const [en, tr] of Object.entries(LAB_LABELS)) {
    if (
      display === en || tr.ro === display || tr.en === display ||
      strip(en) === target || strip(tr.ro) === target || strip(tr.en) === target
    ) {
      return en.toLowerCase();
    }
  }
  return target;
}
